import { useParams } from "react-router-dom";

import { globalApi } from "@/lib/api";
import { rememberedVessel } from "@/lib/currentVessel";
import { useAsync } from "@/lib/hooks";
import { useSession } from "@/lib/session";
import { useVessels } from "@/routes/VesselRoute";
import type { VesselSummary } from "@/types";

import type { NavEntry } from "@/components/TopBar";

//: Paths under `/v/:id`, written without the prefix so the list stays
//: readable, and deliberately *not* as React Router relative links: a bare
//: "log" resolves against whatever depth the current route sits at, so it
//: would reach /v/1/history/log from the history screen. Absolute paths built
//: from the id mean the same thing from everywhere — including from pages
//: that have no pool in their URL at all.
const NAV = [
  { to: "", label: "Now", end: true },
  { to: "/log", label: "Log" },
  { to: "/charts", label: "Trends" },
  { to: "/history", label: "History" },
];

//: What used to be the More screen: the things you touch a few times a
//: season rather than a few times a week, as a dropdown rather than a page
//: of links. The export is a file, so it is a download link, and the help
//: pages are public and absolute.
const MORE = [
  { to: "/equipment", label: "Equipment" },
  { to: "/events", label: "Events" },
  { to: "/pump", label: "Pump schedule" },
  { to: "/gallery", label: "Photos" },
  { to: "/settings", label: "Setup" },
];
const HELP = [
  { to: "/guide", label: "How to manage a pool" },
  { to: "/guide/testing", label: "Testing procedures" },
];

/**
 * The top bar's entries for one pool, or just the help when there is none.
 *
 * Without a pool there is nothing for Now, Log or Trends to point at, so the
 * bar carries Home and the guide and no more — a household that has not set
 * up yet, or a visitor who is not signed in.
 */
export function vesselNav(vesselId: number | null): NavEntry[] {
  if (vesselId === null) {
    return [{ label: "More", sections: [{ title: "Help", items: HELP }] }];
  }
  const prefix = `/v/${vesselId}`;
  return [
    ...NAV.map((item) => ({ ...item, to: `${prefix}${item.to}` })),
    {
      label: "More",
      sections: [
        {
          title: "This pool",
          items: [
            ...MORE.map((item) => ({ ...item, to: `${prefix}${item.to}` })),
            { to: `/api/vessels/${vesselId}/export`, label: "Export this pool as a file", download: true },
          ],
        },
        { title: "Help", items: HELP },
      ],
    },
  ];
}

/**
 * Which pool the bar is about, from wherever the page is.
 *
 * In order: the one in the URL; the one you last looked at, if it is still
 * yours; the first one in the household. The list comes from the route's
 * context when there is a route, and from one fetch otherwise, so a page
 * outside `/v/` still knows the household's pools for the switcher.
 */
export function useResolvedVessel(): { id: number | null; vessels: VesselSummary[] } {
  const { vesselId } = useParams<{ vesselId: string }>();
  const fromRoute = useVessels();
  const { status } = useSession();
  const needFetch = status.authenticated && status.setup_complete && !vesselId && fromRoute.length === 0;
  const fetched = useAsync<VesselSummary[]>(
    () => (needFetch ? globalApi.get<VesselSummary[]>("/api/vessels").catch(() => []) : Promise.resolve([])),
    [needFetch],
  );
  const vessels = fromRoute.length ? fromRoute : (fetched.data ?? []);

  let id: number | null = null;
  if (vesselId && Number.isFinite(Number(vesselId))) {
    id = Number(vesselId);
  } else {
    const remembered = rememberedVessel();
    if (remembered !== null && vessels.some((v) => v.id === remembered)) id = remembered;
    else id = vessels[0]?.id ?? null;
  }
  return { id, vessels };
}
